import {
  getServiceabilityRoadsideAssistanceJob,
  matchPartyRoadsideAssistance,
  postCreateRsaJobJoinOnRoad,
  getCheckEligibility,
  isTrackableUrl,
  getShouldJeopardy,
  postCreateRsaJobHtml,
  postCreateParty,
  postCreateQuote,
  postFinaliseQuote,
  postRsaSubmitPurchase,
  postSendRoadsideGenericInvoiceEmail,
  postUpgradeRoadsideVehicle,
  postRsaSendUpgradeRoadsideEmail,
  changeRegoUrl,
  getFleetServiceByRegoAndServiceNumber,
  postCreateRsaFleetJobHtml,
  checkIsMissingResidentialAddressUrl,
  putUpdatePostalAddress,
} from '@/constants/forms-api.const';
import { RequestServices as _http } from '@/services/common/request.services';
import {
  RoadsideAssistance,
  AssistanceMatchPartyRequest,
  AssistanceMatchPartyResponse,
} from '@/interfaces/entities/roadside-assistance.interface';
import {
  CreateRoadsideJobRequest,
  CreateRoadsideJobResponse,
} from '@/interfaces/entities/jor-create-rsa-job.interface';
import {
  UpgradeRoadsideAssistanceProductResponse,
  CreateAssistanceQuoteResponse,
} from '@/interfaces/entities/ur-upgrade-rsa-product.interface';
import { IsEligibleResponse } from '@/interfaces/entities/rsa-check-eligibility.interface';
import { CreatePartyRequest, CreatePartyResponse } from '@/interfaces/entities/create-party.interface';
import { CreateQuoteRequest, CreateQuoteResponse } from '@/interfaces/entities/create-quote.interface';
import { FinaliseQuoteResponse } from '@/interfaces/entities/finalise-quote.interface';
import { GenericInvoiceEmailRequest } from '@/interfaces/entities/generic-invoice-email-request.interface';
import { UpgradeRoadsideConfirmationEmailRequest } from '@/interfaces/entities/upgrade-roadside-confirmation-email-request.interface';
import { UpgradeRoadsideVehicleRequest } from '@/interfaces/entities/upgrade-roadside-vehicle-request.interface';
import {
  getHasExistingPolicyForRego,
  getEligibleProductForUpgrade,
  getUpgradeQuotes,
} from '../../constants/forms-api.const';
import { FleetServiceModel } from '@/interfaces/entities/fleet-service-model.interface';

export const RoadsideAssistanceService = {
  async getServiceability(latitude: number, longitude: number): Promise<RoadsideAssistance> {
    return await _http.HttpGet(
      getServiceabilityRoadsideAssistanceJob.replace('{0}', latitude.toString()).replace('{1}', longitude.toString())
    );
  },

  /**
   * Match Party Roadside Assistance Job
   * @param payload
   */
  async matchParty(payload: AssistanceMatchPartyRequest): Promise<AssistanceMatchPartyResponse> {
    return await _http.HttpPost(matchPartyRoadsideAssistance, payload);
  },

  /**
   * Create Party for new member
   *
   * @param body
   * @returns
   */
  async createParty(body: CreatePartyRequest): Promise<CreatePartyResponse | null> {
    try {
      return await _http.HttpPost(postCreateParty, body);
    } catch (error) {
      console.error('Failed to create party for new member');
      return null;
    }
  },

  /**
   * Create roadside assistance job for Join on Road
   */
  async createRsaJobJoinOnRoad(payload: CreateRoadsideJobRequest): Promise<CreateRoadsideJobResponse> {
    return await _http.HttpPost(postCreateRsaJobJoinOnRoad, payload);
  },

  /**
   * Check eligibility
   */
  async checkEligibility(
    vehicleRegistration: string,
    d365Id?: string | null,
    membershipCardNumber?: string | null
  ): Promise<IsEligibleResponse> {
    return await _http.HttpGet(
      getCheckEligibility
        .replace('{0}', vehicleRegistration.toString())
        .replace('{1}', d365Id?.toString() ?? '')
        .replace('{2}', membershipCardNumber?.toString() ?? '')
    );
  },
  /**
   * Create Quote
   *
   * @param body
   * @returns
   */
  async createQuote(body: CreateQuoteRequest): Promise<CreateQuoteResponse | null> {
    try {
      return await _http.HttpPost(postCreateQuote, body);
    } catch (error) {
      console.error('Failed to create quote');
      return null;
    }
  },

  /**
   * Finalise Quote
   *
   * @param apttusQuoteId
   * @param apttusCartId
   * @returns
   */
  async finaliseQuote(apttusQuoteId: string, apttusCartId: string): Promise<FinaliseQuoteResponse | null> {
    try {
      return await _http.HttpPost(postFinaliseQuote.replace('{0}', apttusQuoteId).replace('{1}', apttusCartId));
    } catch (error) {
      console.error('Failed to finalise quote');
      return null;
    }
  },

  /**
   * Check dispatch group traceability
   */
  async isTrackable(dispatchGroup: string): Promise<boolean> {
    return await _http.HttpGet(isTrackableUrl.replace('{0}', dispatchGroup));
  },

  /**
   * Check policy status and decide whether to Jeopardy
   */
  async shouldJeopardy(policyStatus: string): Promise<boolean> {
    return await _http.HttpGet(getShouldJeopardy.replace('{0}', policyStatus));
  },

  /**
   * Create roadside assistance job for Html (called by Join on Road in some cases)
   */
  async createRsaJobHtml(
    payload: Partial<CreateRoadsideJobRequest>,
    isFleet: boolean = false
  ): Promise<CreateRoadsideJobResponse> {
    if (isFleet) {
      return await _http.HttpPost(postCreateRsaFleetJobHtml, payload);
    }
    return await _http.HttpPost(postCreateRsaJobHtml, payload);
  },

  /**
   * Submit Purchase
   *
   * @param body
   * @returns
   */
  submitPurchase: (body: Record<string, any>): Promise<any> => {
    return _http.HttpPost(postRsaSubmitPurchase, body);
  },

  /**
   * Send Buy Roadside and JOR invoice email (confirmation email for RSA)
   *
   * @param body
   * @returns
   */
  sendGenericInvoiceEmail: (body: Partial<GenericInvoiceEmailRequest>): Promise<any> => {
    return _http.HttpPost(postSendRoadsideGenericInvoiceEmail, body);
  },

  /**
   * Send Upgrade Roadside Email (confirmation email for RSA)
   *
   * @param body
   * @returns
   */
  sendUpgradeRoadsideEmail: (body: Partial<UpgradeRoadsideConfirmationEmailRequest>): Promise<any> => {
    return _http.HttpPost(postRsaSendUpgradeRoadsideEmail, body);
  },

  async upgradeRoadsideVehicle(body: UpgradeRoadsideVehicleRequest): Promise<any> {
    return await _http.HttpPost(postUpgradeRoadsideVehicle, body);
  },

  /**
   * Returns whether the specified apttusId has an existing non-canceled RSA policy for the rego.
   */
  async hasExistingPolicyForRego(apttusId: string, vehicleRegistration: string): Promise<boolean | null> {
    return await _http.HttpGet(
      getHasExistingPolicyForRego.replace('{0}', apttusId).replace(`{1}`, vehicleRegistration)
    );
  },

  /**
   * Returns whether the specified apttusId has a product eligible for upgrade for the provided policyId
   */
  async getEligibleProductForUpgrade(
    apttusId: string,
    policyId: string
  ): Promise<UpgradeRoadsideAssistanceProductResponse | null> {
    return await _http.HttpGet(getEligibleProductForUpgrade.replace('{0}', apttusId).replace(`{1}`, policyId));
  },

  /**
   * Returns an array of Assistance Quotes for the provided apttus id, product code, policy price and asset line item id
   */
  async getUpgradeQuotes(
    apttusId: string,
    productCode: string,
    policyPrice: number,
    assetLineItemId: string
  ): Promise<CreateAssistanceQuoteResponse[] | null> {
    return await _http.HttpGet(
      getUpgradeQuotes
        .replace('{0}', apttusId)
        .replace(`{1}`, productCode)
        .replace(`{2}`, policyPrice.toString())
        .replace(`{3}`, assetLineItemId)
    );
  },

  /**
   * Update rsa rego number
   * @param newRego
   * @param policyId
   */
  async changeRego(newRego: string, policyId: string): Promise<void> {
    await _http.HttpPost(changeRegoUrl.replace('{0}', newRego).replace('{1}', policyId));
  },

  async getFleetServiceByRegoAndServiceNumber(
    rego: string,
    serviceNumber: string | null
  ): Promise<FleetServiceModel | null> {
    let url = getFleetServiceByRegoAndServiceNumber.replace('{0}', rego);
    if (serviceNumber) {
      url += `&serviceNumber=${serviceNumber}`;
    }
    return await _http.HttpGet(url);
  },

  //--------------------------------------------------------------------
  // Protecht 1149549 DSQ-2173 D365 missing residential address
  //--------------------------------------------------------------------
  checkIsMissingResidentialAddress(d365Id: string): Promise<boolean> {
    return _http.HttpGet(checkIsMissingResidentialAddressUrl.replace('{0}', d365Id));
  },

  updateResidentialAddress(d365Id: string, residentialAddress: Record<string, string | undefined>): Promise<void> {
    return _http.HttpPut(
      putUpdatePostalAddress.replace('{0}', d365Id) + `&addressType=Residential`,
      residentialAddress
    );
  },
  // //--------------------------------------------------------------------
} as const;
